import React from "react"
import { SortDir, SortState, TableColumn } from "./types"
import { sortRows, filterBySearch, paginate } from "./tableUtils"

export interface UseTableStateArgs<T> {
  data: T[]
  columns: TableColumn<T>[]

  // Sort — controlled when onSortChange is provided.
  sort?: SortState
  onSortChange?: (next: SortState) => void
  defaultSort?: (a: T, b: T) => number

  // Search — uncontrolled input, client-side compute (undefined => no search).
  searchTerm?: string

  // Pagination — server mode when pageCount is provided (no client slicing);
  // otherwise client mode when itemsPerPage is provided.
  itemsPerPage?: number
  page?: number
  pageCount?: number
  onPageChange?: (page: number) => void
}

export interface UseTableStateResult<T> {
  // Rows to actually render (post sort/filter/paginate for whichever concerns
  // this table owns).
  rows: T[]
  // Count after filtering but before pagination (for external pagers).
  filteredCount: number
  sortState: SortState
  currentPage: number
  totalPages: number
  handleSort: (columnSortKey: string, dir: SortDir) => void
  handleClearSort: () => void
  setPage: (page: number) => void
}

// Orchestrates a table's sort / search / pagination, choosing controlled or
// uncontrolled behaviour per concern based on which props are supplied.
export const useTableState = <T,>(args: UseTableStateArgs<T>): UseTableStateResult<T> => {
  const { data, columns, sort, onSortChange, defaultSort, searchTerm, itemsPerPage, page, pageCount, onPageChange } =
    args

  const sortControlled = !!onSortChange
  const [internalSort, setInternalSort] = React.useState<SortState>(null)
  const [internalPage, setInternalPage] = React.useState(1)

  const sortState = sortControlled ? sort ?? null : internalSort
  const pageControlled = page != null
  const currentPage = pageControlled ? (page as number) : internalPage

  const handleSort = (columnSortKey: string, dir: SortDir) => {
    if (sortControlled) onSortChange!({ key: columnSortKey, dir })
    else setInternalSort({ key: columnSortKey, dir })
  }
  const handleClearSort = () => {
    if (sortControlled) onSortChange!(null)
    else setInternalSort(null)
  }
  const setPage = (next: number) => {
    if (onPageChange) onPageChange(next)
    if (!pageControlled) setInternalPage(next)
  }

  // 1. Sort — only when this table owns sorting (uncontrolled). In controlled
  //    mode the parent supplies already-sorted data.
  let processed = data
  if (!sortControlled) {
    if (sortState) {
      const col = columns.find((c) => (c.sortKey ?? c.key) === sortState.key)
      const accessor = col?.sortAccessor ?? ((row: T) => (row as Record<string, unknown>)[col?.key ?? sortState.key])
      processed = sortRows(processed, accessor, sortState.dir)
    } else if (defaultSort) {
      processed = [...processed].sort(defaultSort)
    }
  }

  // 2. Search — client-side, only when a searchTerm is supplied.
  if (searchTerm != null) {
    processed = filterBySearch(processed, columns, searchTerm)
  }

  const filteredCount = processed.length

  // 3. Pagination.
  let rows = processed
  let totalPages = 1
  if (pageCount != null) {
    // Server mode: data is already the current page.
    totalPages = Math.max(1, pageCount)
  } else if (itemsPerPage != null) {
    // Client mode: slice to the current page.
    totalPages = Math.max(1, Math.ceil(filteredCount / itemsPerPage))
    const safePage = Math.min(currentPage, totalPages)
    rows = paginate(processed, safePage, itemsPerPage)
  }

  // Clamp the reported page to the available range in both modes, so an
  // out-of-range page (e.g. a server-supplied page past the end) never leaves
  // the pager highlighting a page with no rows.
  const clampedPage = Math.min(currentPage, totalPages)

  // Push the clamp back into the owning state. Reporting a clamped page isn't
  // enough in server mode: if pageCount shrinks, the parent would otherwise keep
  // requesting an out-of-range page and render an empty result indefinitely.
  React.useEffect(() => {
    if (currentPage > totalPages) setPage(totalPages)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, totalPages])

  return {
    rows,
    filteredCount,
    sortState,
    currentPage: clampedPage,
    totalPages,
    handleSort,
    handleClearSort,
    setPage,
  }
}
